<script setup>
import { computed, ref, watch } from "vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import InputError from "@/Components/InputError.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { Inertia } from "@inertiajs/inertia";
import { useHelpers } from "@/helpers";

defineProps({
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  }
});
const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
watch(projectProperty, (value) => {
  project = projectProperty.value;
});

const makeAllExternal = () => {
  Inertia.post(
    route('admin.projects.notes.makeAllExternal', { projectid: project.pid }),
    {},
    { preserveScroll: true }
  )
}

const saving = ref(false);
const form = useForm({
  _method: 'PUT',
  note: {
    title: null,
    notes: null,
    prj_note_type: 'i'
  }
});

const createNote = () => {
  form.post(route('admin.projects.update', { project: project.pid }), {
    preserveScroll: true,
    onSuccess() {
      form.reset();
    },
    onFinish() {
      saving.value = false;
    }
  });
  saving.value = false;
}

const deleteNote = (noteId) => {
  Inertia.post(route('admin.projects.notes.destroy', {
    projectid: project.pid,
    noteid: noteId
  }), { _method: 'DELETE' }, {
    preserveScroll: true,
  });
}
const createdAt = (createdDate) => {
  return dayjs.tz(parseInt(createdDate) * 1000).format('MMMM D, YYYY hh:mm A');
}

const noteType = (noteType) => {
  if (!['i', 'int'].includes(noteType)) {
    return 'External';
  }

  return 'Internal';
}
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <!--<div class="flex justify-end mb-3" v-if="!hideSave && helpers.userCanManageProject(currentUser)">
      <PrimaryButton @click.prevent="makeAllExternal()" :disabled="saving || project.notes.length === 0">
        Make all external
      </PrimaryButton>
    </div>-->
    <table class="min-w-full w-full text-left text-sm font-light" v-if="project.notes.length > 0">
      <thead class="border-b font-medium dark:border-neutral-500">
        <tr>
          <th scope="col" class="px-4 py-2">Created By</th>
          <th scope="col" class="px-4 py-2">Created At</th>
          <th scope="col" class="px-4 py-2">Title</th>
          <th scope="col" class="px-4 py-2">Notes</th>
          <th scope="col" class="px-4 py-2">Type</th>
          <th scope="col" class="px-4 py-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="note in project.notes" class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
          :key="note.notesid">
          <td class="px-4 py-2 break-words" v-text="note.creator.full_name"></td>
          <td class="px-4 py-2 break-words" v-text="createdAt(note.createdDate)"></td>
          <td class="px-4 py-2 break-words" v-text="note.title"></td>
          <td class="px-4 py-2 whitespace-pre-wrap">
            <span v-text="note.notes"></span>
          </td>
          <td class="px-4 py-2 break-words" v-text="noteType(note.prj_note_type)"></td>
          <td class="px-4 py-2 break-words">
            <span class="fa fa-trash cursor-pointer" @click.prevent="deleteNote(note.notesid)"
              v-if="!hideSave && helpers.userCanManageProject(currentUser)"></span>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="project.notes.length === 0">No records...</p>
  </div>

  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md"
    v-if="!hideSave && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))">
    <div class="sm:grid grid-cols-6 gap-6">
      <!-- Note Title -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="title" value="Title" :required="true" />
        <TextInput id="title" type="text" class="mt-1 block w-full" autocomplete="project-name"
          v-model="form.note.title"
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
        <InputError class="mt-2" :message="form.errors['note.title']" />
      </div>
      <!-- Description -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="note" value="Note" :required="true" />
        <textarea rows="4" id="garment-description" v-model="form.note.notes"
          class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></textarea>
        <InputError class="mt-2" :message="form.errors['note.notes']" />
      </div>
      <!-- Type -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel value="Type" />
        <label for="type-external" class="mr-5">
          <input type="radio" v-model="form.note.prj_note_type" value="e" id="type-external"
            :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))">
          <span class="ml-1">External</span>
        </label>
        <label for="type-internal" v-if="!helpers.userIsVendor(currentUser)">
          <input type="radio" v-model="form.note.prj_note_type" value="i" id="type-internal"
            :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))">
          <span class="ml-1">Internal</span>
        </label>
        <InputError class="mt-2" :message="form.errors['note.prj_note_type']" />
      </div>
    </div>

  </div>
  <div
    class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="createNote()" :disabled="saving" v-if="hideSave === false">Save</PrimaryButton>
  </div>
</template>
